import { isValid, parse } from "date-fns"
import Papa from "papaparse"

export type CSVParseConfig = {
  separator: string
  dateFormat: string
  decimalSeparator: "," | "."
}

export type ParsedCSVRow = Record<string, string>

export type ParsedCSV = {
  headers: string[]
  rows: ParsedCSVRow[]
  errors: Papa.ParseError[]
}

/**
 * Parse a CSV file with the given configuration
 */
export function parseCSV(file: File, config: CSVParseConfig): Promise<ParsedCSV> {
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: true,
      delimiter: config.separator,
      skipEmptyLines: true,
      chunkSize: 100,
      worker: true,
      encoding: "UTF-8",
      fastMode: true,
      // transformHeader: (header: string) => header.trim(),
      // transform: (value: string) => value.trim(),
      complete: (results: Papa.ParseResult<ParsedCSVRow>) => {
        if (results.errors.length > 0 && results.data.length === 0) {
          reject(new Error("Failed to parse CSV file"))
          return
        }

        resolve({
          headers: results.meta.fields || [],
          rows: results.data as ParsedCSVRow[],
          errors: results.errors,
        })
      },
      error: (error: Error) => {
        reject(error)
      },
    })
  })
}

/**
 * Parse a date string with the given format
 */
export function parseDate(dateString: string, formatString: string): Date | null {
  if (!dateString || dateString.trim() === "") {
    return null
  }

  // Try parsing with the provided format
  const parsed = parse(dateString.trim(), formatString, new Date())
  if (isValid(parsed)) {
    return parsed
  }

  // Try common ISO formats
  const isoDate = new Date(dateString)
  if (!isNaN(isoDate.getTime())) {
    return isoDate
  }

  return null
}

/**
 * Parse a number string with the given decimal separator
 */
export function parseNumber(value: string, decimalSeparator: "," | "."): number | null {
  if (!value || value.trim() === "") {
    return null
  }

  // Remove any currency symbols, spaces, and thousand separators
  let cleaned = value.trim().replace(/[^\d.,-]/g, "")

  // Handle negative numbers
  const isNegative = cleaned.startsWith("-")
  cleaned = cleaned.replace(/^-/, "")

  // Convert decimal separator to dot if needed
  if (decimalSeparator === ",") {
    cleaned = cleaned.replace(/\./g, "").replace(",", ".")
  } else {
    cleaned = cleaned.replace(/,/g, "")
  }

  const parsed = parseFloat(cleaned)
  if (isNaN(parsed)) {
    return null
  }

  return isNegative ? -parsed : parsed
}

/**
 * Get sample rows from parsed CSV (first 5 rows)
 */
export function getSampleRows(parsedCSV: ParsedCSV, count: number = 5): ParsedCSVRow[] {
  return parsedCSV.rows.slice(0, count)
}
